import React, { useEffect, useState } from 'react';
import { Alert, Button, Input, Space, Spin, Tooltip } from 'antd';
import { CheckOutlined, DeleteOutlined, PictureOutlined } from '@ant-design/icons';
import { listTenantsAdmin } from 'api/tenantsAdmin';
import { getMyTenantLogo } from 'api/tenantSelf';
import { apiAssetUrl } from 'utils/apiAssetUrl';

// Editor del campo `template.logo`. Acepta cualquier URL pública (o path
// relativo servido por la app). El shortcut "Usar logo del tenant" se ofrece
// en dos modos:
//   - `tenantId` (super-admin editando un tenant ajeno): resuelve vía el admin
//     list `/tenants/admin/list`.
//   - `selfTenant` (usuario editando la plantilla de su propio tenant): resuelve
//     vía `/tenants/me/logo` (no requiere super-admin).
// En la práctica todos los PDFs de un tenant usan su mismo logo, así que el 99%
// de las veces solo se toca "Usar logo del tenant".
function LogoUploader({ value, onChange, tenantId, selfTenant = false }) {
  const [tenantLogoUrl, setTenantLogoUrl] = useState(null);
  const [loadingTenantLogo, setLoadingTenantLogo] = useState(false);

  useEffect(() => {
    if (!tenantId && !selfTenant) {
      setTenantLogoUrl(null);
      return undefined;
    }
    let cancelled = false;
    setLoadingTenantLogo(true);
    // tenantId (admin) tiene prioridad; si no, modo self.
    const fetchLogo = tenantId
      ? listTenantsAdmin().then((tenants) => {
          const match = Array.isArray(tenants)
            ? tenants.find((t) => Number(t.id) === Number(tenantId))
            : null;
          return match?.logo_url || null;
        })
      : getMyTenantLogo().then((data) => data?.logo_url || null);
    fetchLogo
      .then((logoUrl) => {
        if (!cancelled) setTenantLogoUrl(logoUrl);
      })
      .catch(() => {
        if (!cancelled) setTenantLogoUrl(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingTenantLogo(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, selfTenant]);

  const applyTenantLogo = () => {
    if (tenantLogoUrl) onChange(tenantLogoUrl);
  };

  const hasValue = !!value;
  const isUsingTenantLogo = hasValue && value === tenantLogoUrl;

  return (
    <Space direction="vertical" size="small" style={{ width: '100%' }}>
      {/* Shortcut: logo del tenant */}
      {tenantId || selfTenant ? (
        loadingTenantLogo ? (
          <Spin size="small" />
        ) : tenantLogoUrl ? (
          <Space align="center" size="middle" wrap>
            <img
              src={apiAssetUrl(tenantLogoUrl)}
              alt="Logo del tenant"
              style={{
                maxHeight: 48,
                maxWidth: 120,
                border: '1px solid #e8e8e8',
                borderRadius: 4,
                padding: 4,
                background: '#fff',
              }}
            />
            <Tooltip
              title={
                isUsingTenantLogo
                  ? 'Esta plantilla ya usa el logo del tenant'
                  : 'Completa el campo con la URL del logo configurado para este tenant'
              }
            >
              <Button
                icon={isUsingTenantLogo ? <CheckOutlined /> : <PictureOutlined />}
                onClick={applyTenantLogo}
                disabled={isUsingTenantLogo}
              >
                {isUsingTenantLogo ? 'Usando logo del tenant' : 'Usar logo del tenant'}
              </Button>
            </Tooltip>
          </Space>
        ) : (
          <Alert
            type="info"
            showIcon
            message="Este tenant no tiene un logo configurado."
            description="Subí el logo desde la página del tenant (Administración → Tenants → Subir logo) o pegá una URL pública abajo."
          />
        )
      ) : null}

      {/* Preview del valor actual (si es URL distinta al logo del tenant) */}
      {hasValue && !isUsingTenantLogo ? (
        <div>
          <div style={{ fontSize: 12, color: '#8c8c8c', marginBottom: 4 }}>Vista previa:</div>
          <img
            src={apiAssetUrl(value)}
            alt="Logo actual"
            style={{
              maxHeight: 48,
              maxWidth: 120,
              border: '1px solid #e8e8e8',
              borderRadius: 4,
              padding: 4,
              background: '#fff',
            }}
            onError={(e) => {
              e.currentTarget.style.display = 'none';
            }}
          />
        </div>
      ) : null}

      {/* Input de URL libre + clear */}
      <Space.Compact style={{ width: '100%' }}>
        <Input
          placeholder="URL del logo (ej: https://... o /uploads/logo.png)"
          value={value || ''}
          onChange={(e) => onChange(e.target.value || null)}
        />
        {value ? (
          <Button
            danger
            icon={<DeleteOutlined />}
            onClick={() => onChange(null)}
            title="Quitar logo"
          />
        ) : null}
      </Space.Compact>
    </Space>
  );
}

export default LogoUploader;
